import type { Module } from '@pc/core';
import { probeSchemaIsExposed } from '@pc/db';
import { lazyWithReload } from '@pc/ui';
import {
  registerOrderDetailSlot,
  registerPublicProposalSlot,
} from '@pc/modules-orders/slots';
import './events';
import { AttachedContractsCard } from './slots/AttachedContractsCard';
import { PublicContractsBlock } from './slots/PublicContractsBlock';

const ContractsListPage = lazyWithReload(() => import('./pages/ContractsListPage'));
const ContractNewPage = lazyWithReload(() => import('./pages/ContractNewPage'));
const ContractDetailPage = lazyWithReload(() => import('./pages/ContractDetailPage'));
const ContractsDashboardWidget = lazyWithReload(
  () => import('./widgets/ContractsDashboardWidget'),
);

// Register slot extensions at module load time. The orders module exposes
// two named slots; we plug into both. This is the only correct way for
// contracts to surface UI inside another module's pages without violating
// the "no cross-module internals" rule -- orders defines the slot
// contract, contracts uses it.
registerOrderDetailSlot({
  id: 'contracts.attached-card',
  order: 20,
  render: AttachedContractsCard,
});
registerPublicProposalSlot({
  id: 'contracts.public-block',
  order: 10,
  render: PublicContractsBlock,
});

export const contractsModule: Module = {
  id: 'contracts',
  name: 'Contracts',
  description:
    'Org-managed legal documents attached to proposals. Versioned, snapshotted, ESIGN-compliant.',
  basePath: '/contracts',
  routes: [
    { path: '/', element: <ContractsListPage /> },
    { path: '/new', element: <ContractNewPage /> },
    { path: '/:id', element: <ContractDetailPage /> },
  ],
  navItems: [{ label: 'Contracts', to: '/contracts', order: 25 }],
  dashboardWidgets: [
    {
      id: 'contracts-recent',
      span: 1,
      order: 30,
      render: ContractsDashboardWidget,
    },
  ],
  events: {
    publishes: [
      'contract.template_created',
      'contract.template_published',
      'contract.template_archived',
      'contract.attached_to_order',
      'contract.detached_from_order',
      'contract.accepted_by_client',
    ],
  },
  healthProbe: () => probeSchemaIsExposed('contracts', 'v_order_contract_status'),
};
